import { NavLink, useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaBars, FaTimes, FaHome, FaChevronDown, FaGlobe } from "react-icons/fa";
import logo from "../../assets/images/amass-pros-private-limited-logo.webp";

import "./Navbar.css";
import SearchBar from "../../pages/SearchBar/SearchBar";

function Navbar() {
  const { t, i18n } = useTranslation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [shoppingOpen, setShoppingOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const savedLang = localStorage.getItem("lang");
    if (savedLang) i18n.changeLanguage(savedLang);
  }, [i18n]);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setServicesOpen(false);
    setShoppingOpen(false);
    setLanguageOpen(false);
  };

  const isHoverCapable = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(hover: hover)").matches;

  const isIpadView = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(hover: none)").matches &&
    window.innerWidth >= 769 &&
    window.innerWidth <= 1024;

  const toggleServices = () => {
    if (window.innerWidth <= 768 || isIpadView()) {
      setServicesOpen((prev) => !prev);
    }
  };

  const toggleShopping = () => {
    if (window.innerWidth <= 768 || isIpadView()) {
      setShoppingOpen((prev) => !prev);
    }
  };

  const toggleLanguage = () => {
    if (window.innerWidth <= 768 || isIpadView()) {
      setLanguageOpen((prev) => !prev);
    }
  };

  const languageOptions = [
    { code: "en", label: "English - EN" },
    { code: "hi", label: "हिन्दी - HI" },
    { code: "ta", label: "தமிழ் - TA" },
    { code: "te", label: "తెలుగు - TE" },
    { code: "kn", label: "ಕನ್ನಡ - KN" },
    { code: "ml", label: "മലയാളം - ML" },
    { code: "bn", label: "বাংলা - BN" },
    { code: "mr", label: "मराठी - MR" },
    { code: "gu", label: "ગુજરાતી - GU" },
    { code: "ur", label: "اردو - UR" },
    { code: "or", label: "ଓଡ଼ିଆ - OR" }
  ];

  const currentLanguageLabel =
    languageOptions.find((option) => option.code === i18n.language)?.label ||
    "English - EN";

  if (location.pathname === "/login") return null;

  return (
    <header className="navbar">
      <div className="navbar-container">

        
        <NavLink to="/" onClick={closeMenu}>
          <img
            src={logo}
            alt={t("logoAlt")}
            className="logo"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </NavLink>

        
        <nav className={`nav-links ${menuOpen ? "open" : ""}`}>

          <NavLink to="/" end onClick={closeMenu}>
            <FaHome /> {t("Home")}
          </NavLink>

          <NavLink to="/about" onClick={closeMenu}>
            {t("About US")}
          </NavLink>

          
          <li
            className="dropdown"
            onMouseEnter={() => {
              if (isHoverCapable()) setServicesOpen(true);
            }}
            onMouseLeave={() => {
              if (isHoverCapable()) setServicesOpen(false);
            }}
          >
            <span
              className={`services dropdown-toggle ${servicesOpen ? "open" : ""}`}
              onClick={toggleServices}
              aria-expanded={servicesOpen}
            >
              <span>{t("Services")}</span>
              <FaChevronDown className="dropdown-caret" />
            </span>

            {servicesOpen && (
              <div className="dropdown-menu">
                <Link to="/our-role" className="dropdown-item" onClick={closeMenu}>
                  {t("Our Role")}
                </Link>

                <Link to="/our-invention" className="dropdown-item" onClick={closeMenu}>
                  {t("Our Invention")}
                </Link>

                <Link to="/overview" className="dropdown-item" onClick={closeMenu}>
                  {t("Overview")}
                </Link>
              </div>
            )}
          </li>

          
          <li
            className="dropdown"
            onMouseEnter={() => {
              if (isHoverCapable()) setShoppingOpen(true);
            }}
            onMouseLeave={() => {
              if (isHoverCapable()) setShoppingOpen(false);
            }}
          >
            <span
              className={`services dropdown-toggle ${shoppingOpen ? "open" : ""}`}
              onClick={toggleShopping}
              aria-expanded={shoppingOpen}
            >
              <span>{t("Shopping Centre")}</span>
              <FaChevronDown className="dropdown-caret" />
            </span>

            {shoppingOpen && (
              <div className="dropdown-menu">
                <Link to="/shopping-centre" className="dropdown-item" onClick={closeMenu}>
                  {t("What is RSC?")}
                </Link>

                <a
                  href="https://amasspros.in"
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                >
                  {t("Shopping")}
                </a>
              </div>
            )}
          </li>

          
          <NavLink to="/contact" onClick={closeMenu}>
            {t("Contact Us")}
          </NavLink>

          
          <SearchBar />

          
          <div className="divider">
            <a
              href="https://amasspros.in/login"
              className="login-btn"
              onClick={closeMenu}
            >
              {t("Login")}
            </a>
          </div>

          
          <li
            className="dropdown language-dropdown"
            onMouseEnter={() => {
              if (isHoverCapable()) setLanguageOpen(true);
            }}
            onMouseLeave={() => {
              if (isHoverCapable()) setLanguageOpen(false);
            }}
          >
            <span
              className={`services dropdown-toggle ${languageOpen ? "open" : ""}`}
              onClick={toggleLanguage}
              aria-expanded={languageOpen}
            >
              <FaGlobe className="lang-icon" />
              <span className="lang-current">{currentLanguageLabel.split(" - ")[0]}</span>
              <FaChevronDown className="dropdown-caret" />
            </span>

            {languageOpen && (
              <div className="dropdown-menu lang-menu">
                <div className="lang-title">{t("changeLanguage")}</div>
                {languageOptions.map((option) => {
                  const isActive = i18n.language === option.code;
                  return (
                    <button
                      key={option.code}
                      type="button"
                      className={`lang-option ${isActive ? "active" : ""}`}
                      onClick={() => {
                        changeLanguage(option.code);
                        setLanguageOpen(false);
                        closeMenu();
                      }}
                    >
                      <span className="lang-radio" aria-hidden="true" />
                      <span className="lang-text">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </li>
        </nav>

        
        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>

      </div>
    </header>
  );
}

export default Navbar;





